import { create } from 'zustand';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import { apiNotification } from 'src/api/notifications';
import { deleteCookie, getCookie } from 'src/utils/cookies';
import {
  emailCodeCookie,
  emailNotificationProperty,
  emailServiceAddress,
  emailServiceMode,
  smsCodeCookie,
  smsNotificationProperty,
  smsServiceMode,
  smsServicePhoneNumber,
} from 'src/consts/cookie-consts';
import { isChannelVerified } from './verified-session';

export type TChannel = 'email' | 'sms';

export const CHANNELS: TChannel[] = ['email', 'sms'];

// The verified-session cookie that carries each channel's verification_id
// (1h TTL, written by VerifyContactModal on a successful verify).
export const CODE_COOKIE: Record<TChannel, string> = {
  email: emailCodeCookie,
  sms: smsCodeCookie,
};

/**
 * Every cookie ONE channel's verified session is made of.
 *
 * Logging out of the phone number must not take an email session with it —
 * the two are independent verifications of two different contacts, and the
 * citizen may well be managing both. `clearNotificationSession()`
 * (src/utils/notification-session.ts) is the all-channels variant, used by
 * portal logout.
 */
const CHANNEL_COOKIES: Record<TChannel, string[]> = {
  email: [
    emailCodeCookie,
    emailServiceAddress,
    emailServiceMode,
    emailNotificationProperty,
  ],
  sms: [
    smsCodeCookie,
    smsServicePhoneNumber,
    smsServiceMode,
    smsNotificationProperty,
  ],
};

// PRO-635: a verification belongs to ONE address. The cookies are scoped to the
// portal domain, so reading the code cookie alone carried a session made for
// one address over to whatever address was chosen next. `isChannelVerified`
// only accepts the cookie when its companion property cookie matches.
const verificationIdFor = (channel: TChannel, propertyId: string): string =>
  isChannelVerified(channel, propertyId) ? getCookie(CODE_COOKIE[channel]) : '';

export const DEFAULT_DAYS_BEFORE = 1;

type ChannelState = {
  // Empty string ⇒ this channel is not verified in the current session.
  verificationId: string;
  // Phone / email of the verified contact (for display + re-verify prefill).
  contact: string;
  // The FULL set of container ids subscribed on this channel, as the BACKEND
  // has it. The citizen's unsaved edits live in `draft`, never here — see the
  // type below. The backend POST is a full replace, so we always hold the
  // complete set.
  containerIds: string[];
  loaded: boolean;
};

const emptyChannel = (verificationId = ''): ChannelState => ({
  verificationId,
  contact: '',
  containerIds: [],
  loaded: false,
});

/**
 * The credential is gone, the backend's picture is not.
 *
 * Used when a verification lapses mid-save. Wiping `containerIds` too would
 * throw away the baseline the draft is diffed against, so an unsaved flip
 * would be counted twice ("2 unsaved changes" for one switch) and a shared
 * lead-time value would look safe to commit when the lapsed channel still
 * carries that container. It would also snap every switch on the page to off —
 * the exact lie BEACON-56 is about. Re-verifying re-seeds the set from the
 * backend anyway.
 */
const lapsedChannel = (channel: ChannelState): ChannelState => ({
  verificationId: '',
  contact: '',
  containerIds: channel.containerIds,
  loaded: false,
});

/**
 * BEACON-57: the citizen's unsaved edits, held locally until they press Save.
 *
 * Every flip and every lead-time pick used to POST immediately, so a single
 * accidental tap subscribed or unsubscribed a citizen with nothing to undo it
 * (and the old standalone Notifications page, PRO-635, had already settled on
 * explicit save for exactly this reason). The draft is the same shape as the
 * saved state so the two can be diffed:
 *
 *  - `email` / `sms`: `null` means "no pending change on this channel";
 *    otherwise the COMPLETE desired set, which is also what gets POSTed.
 *  - `daysBefore`: only the containers whose lead time was changed.
 *
 * It is deliberately in-memory only. A draft must not outlive the session
 * (BEACON-57 / PRO-674): portal logout calls `reset()`, logging out of one
 * verification calls `logoutChannel()`, and a reload simply starts empty.
 */
export type TDraft = {
  email: string[] | null;
  sms: string[] | null;
  daysBefore: Record<string, number>;
};

const emptyDraft = (): TDraft => ({ email: null, sms: null, daysBefore: {} });

const sameSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

type TStore = {
  context: { propertyId: string; token: string };
  email: ChannelState;
  sms: ChannelState;
  // One "days before" value per agreement (container), shared across channels.
  daysBefore: Record<string, number>;
  draft: TDraft;

  init: (context: { propertyId: string; token: string }) => void;
  isVerified: (channel: TChannel) => boolean;
  setVerified: (
    channel: TChannel,
    d: { verificationId: string; contact: string }
  ) => Promise<void>;
  setChannel: (containerId: string, channel: TChannel, on: boolean) => void;
  setDaysBefore: (containerId: string, days: number) => void;
  confirmPending: () => Promise<void>;
  discardPending: () => void;
  logoutChannel: (channel: TChannel) => void;
  reset: () => void;
};

/* ---------------------------------------------------------------------------
 * Selectors. Pure functions of the state so a component can pass them straight
 * to `useContainerNotificationsStore(...)` and so the draft/saved merge lives
 * in exactly one place — the switches, the lead-time dropdown, the unsaved
 * count and the POST payload all have to agree on it or the Save bar lies
 * about what will be sent.
 * ------------------------------------------------------------------------- */

/** What the UI shows for a channel: the draft when there is one, else saved. */
export const effectiveContainerIds = (s: TStore, channel: TChannel): string[] =>
  s.draft[channel] ?? s[channel].containerIds;

export const isChannelOn = (
  s: TStore,
  containerId: string,
  channel: TChannel
): boolean => effectiveContainerIds(s, channel).includes(containerId);

export const effectiveDaysBefore = (s: TStore, containerId: string): number =>
  s.draft.daysBefore[containerId] ??
  s.daysBefore[containerId] ??
  DEFAULT_DAYS_BEFORE;

/**
 * Verified, but the citizen's own set has not arrived yet.
 *
 * `init()` re-hydrates a verificationId from the cookies and only then fetches
 * the subscriptions, so for that window every switch reads off while the
 * baseline the draft is diffed against is still empty. An edit made there would
 * be sent as the COMPLETE desired set and unsubscribe everything else, since the
 * POST is a full replace — so the controls disable themselves until this is
 * false, and Save neither posts nor commits anything such a channel might
 * carry. A channel whose GET fails drops its verification instead of sitting
 * here forever (see `seedChannel`), so nothing can get stuck.
 */
export const isChannelSeeding = (s: TStore, channel: TChannel): boolean =>
  !!s[channel].verificationId && !s[channel].loaded;

/**
 * Does the draft have anything to say to this channel?
 *
 * A set change, or a lead-time change on a container this channel carries.
 * Shared by "which channels do we POST" and "which edits cannot be sent at
 * all" so the two cannot disagree about what Save covers — a lead-time-only
 * edit used to fall through the second check and be committed as saved without
 * a request ever being made.
 */
const channelHasPendingEdit = (s: TStore, channel: TChannel): boolean => {
  const draft = s.draft[channel];
  if (draft && !sameSet(draft, s[channel].containerIds)) return true;
  const ids = effectiveContainerIds(s, channel);
  return Object.entries(s.draft.daysBefore).some(
    ([id, days]) =>
      ids.includes(id) && days !== (s.daysBefore[id] ?? DEFAULT_DAYS_BEFORE)
  );
};

/**
 * How many switches / lead times differ from what the backend has.
 *
 * Drives whether the Save bar appears at all, so it must count what a citizen
 * would call a change. Flipping a switch on and straight back off is zero
 * changes (`setChannel` drops the draft back to `null` when it matches saved),
 * and a lead-time edit only counts while the container is actually subscribed
 * somewhere — otherwise an inert dropdown would hold the bar open forever.
 */
export const pendingChangeCount = (s: TStore): number => {
  let count = 0;

  CHANNELS.forEach((channel) => {
    const draft = s.draft[channel];
    if (!draft) return;
    const saved = s[channel].containerIds;
    count += draft.filter((id) => !saved.includes(id)).length;
    count += saved.filter((id) => !draft.includes(id)).length;
  });

  Object.entries(s.draft.daysBefore).forEach(([id, days]) => {
    const subscribed = CHANNELS.some((channel) =>
      effectiveContainerIds(s, channel).includes(id)
    );
    const changed = days !== (s.daysBefore[id] ?? DEFAULT_DAYS_BEFORE);
    if (subscribed && changed) count += 1;
  });

  return count;
};

/**
 * What one Save press actually managed to do.
 *
 *  - `posted`: the set each channel's POST carried, exactly as it went out.
 *  - `sent`: the draft as it stood when the requests left, so an edit made
 *    while they were in flight is told apart from the one that was sent.
 *  - `accepted` / `stillPending`: which channels the backend took, and which
 *    it did not — a failed POST, a lapsed verification, or a channel whose
 *    backend set is not known yet.
 *  - `baselineUnknown`: some channel is still seeding, so nobody can say which
 *    containers it carries.
 */
type TSaveOutcome = {
  posted: Partial<Record<TChannel, string[]>>;
  sent: TDraft;
  accepted: TChannel[];
  stillPending: TChannel[];
  baselineUnknown: boolean;
};

const sameDraftSet = (a: string[] | null, b: string[] | null): boolean =>
  a === b || (!!a && !!b && sameSet(a, b));

/**
 * Fold the result of one Save press into the saved picture.
 *
 * One rule throughout: a channel counts as saved for exactly what its POST
 * carried, never for whatever the draft happens to hold when the request
 * resolves — the switches stay live while saving, so the citizen can edit in
 * that window and that edit has not been sent.
 *
 * Each channel commits on its own because the two are independent verification
 * sessions: one can succeed while the other's has lapsed. Committing the lot
 * would claim a save that never happened; committing nothing would hide that
 * the accepted channel's backend has already moved, and a later Cancel would
 * then revert the UI to a state the backend no longer holds.
 *
 * The SHARED lead-time value commits only when an accepted POST actually
 * carried that container and no still-pending channel does — and not at all
 * while any channel's set is unknown, since it may well carry it.
 */
const commitOutcome = (s: TStore, o: TSaveOutcome): Partial<TStore> => {
  const next: Record<string, unknown> = {};
  const draft: TDraft = { ...s.draft };

  CHANNELS.forEach((channel) => {
    if (o.stillPending.includes(channel)) return;
    const posted = o.posted[channel];
    if (posted && o.accepted.includes(channel)) {
      next[channel] = { ...s[channel], containerIds: posted };
    }
    if (sameDraftSet(s.draft[channel], o.sent[channel])) draft[channel] = null;
  });

  const heldBack = new Set<string>();
  o.stillPending.forEach((channel) =>
    effectiveContainerIds(s, channel).forEach((id) => heldBack.add(id))
  );

  const daysBefore = { ...s.daysBefore };
  const pendingDays: Record<string, number> = { ...s.draft.daysBefore };
  if (!o.baselineUnknown) {
    Object.entries(o.sent.daysBefore).forEach(([id, days]) => {
      if (heldBack.has(id)) return;
      const wasSent = o.accepted.some((channel) =>
        (o.posted[channel] ?? []).includes(id)
      );
      if (!wasSent) return;
      daysBefore[id] = days;
      if (pendingDays[id] === days) delete pendingDays[id];
    });
  }

  draft.daysBefore = pendingDays;
  next.daysBefore = daysBefore;
  next.draft = draft;
  return next as Partial<TStore>;
};

/**
 * Thrown when an edit could not be sent because its channel cannot carry it
 * right now: the verification is gone, or the channel's own subscriptions have
 * not arrived yet. The Save bar turns any rejection into "could not save",
 * which is the honest answer — the citizen's next step is the login panel,
 * which is already telling them so.
 */
export class VerificationRequiredError extends Error {
  constructor() {
    super('Notification channel is not verified');
    this.name = 'VerificationRequiredError';
  }
}

// A backend session (verification_id) is only valid for ~1h. When it lapses,
// the notifications API returns 403 / { detail: 'Forbidden' }.
const isSessionExpired = (e: unknown): boolean => {
  const err = e as {
    response?: { status?: number; data?: { detail?: string } };
  };
  return (
    err?.response?.status === 403 || err?.response?.data?.detail === 'Forbidden'
  );
};

export const useContainerNotificationsStore = create<TStore>()((set, get) => {
  // Full-replace POST of a channel's subscription set, with the effective (ie.
  // draft-aware) lead time per item. On session expiry we clear the channel's
  // verificationId (so the UI re-prompts verification) and re-throw so the
  // caller can decide what is still unsaved.
  const postChannel = async (
    channel: TChannel,
    containerIds: string[]
  ): Promise<void> => {
    const { context } = get();
    const { verificationId } = get()[channel];
    if (!verificationId) return;
    try {
      await apiNotification.mutateUpdateContainers({
        token: context.token,
        propertyId: context.propertyId,
        verificationId,
        containers: containerIds.map((id) => ({
          containerId: id,
          daysBefore: effectiveDaysBefore(get(), id),
        })),
      });
    } catch (e) {
      if (isSessionExpired(e)) {
        set(
          (st) => ({ [channel]: lapsedChannel(st[channel]) }) as Partial<TStore>
        );
      }
      throw e;
    }
  };

  // Seed a channel's subscription set + shared daysBefore from the backend.
  // `authoritative` (email) overrides the shared daysBefore; the other channel
  // only fills gaps so the two never fight over the same key.
  const seedChannel = async (
    channel: TChannel,
    verificationId: string,
    authoritative: boolean
  ): Promise<void> => {
    const { context } = get();
    try {
      const { data }: $TSFixMe =
        await apiNotification.callGetAllNotificationContainers({
          token: context.token,
          propertyId: context.propertyId,
          verificationId,
        });
      const ids: string[] = [];
      const days: Record<string, number> = {};
      (data || []).forEach(({ container, days_before }: $TSFixMe) => {
        if (!container?.id) return;
        ids.push(container.id);
        days[container.id] = days_before ?? DEFAULT_DAYS_BEFORE;
      });
      set(
        (s) =>
          ({
            [channel]: {
              ...s[channel],
              verificationId,
              containerIds: ids,
              loaded: true,
            },
            daysBefore: authoritative
              ? { ...s.daysBefore, ...days }
              : { ...days, ...s.daysBefore },
          }) as Partial<TStore>
      );
    } catch {
      // The api layer surfaces its own error toast. Keeping the verification
      // would leave a channel that claims to be logged in while its baseline
      // is an empty set the citizen never had — and the POST is a full
      // replace, so one flip and Save would unsubscribe everything. Drop the
      // session instead; the login panel offers logging in again.
      set({ [channel]: emptyChannel() } as Partial<TStore>);
    }
  };

  // Which verified channels the draft actually has something to say to. A
  // channel with no set change and no lead-time change among ITS containers is
  // left alone rather than re-POSTing an identical set.
  const channelsToPost = (): TChannel[] =>
    CHANNELS.filter((channel) => {
      const s = get();
      return (
        !!s[channel].verificationId &&
        !isChannelSeeding(s, channel) &&
        channelHasPendingEdit(s, channel)
      );
    });

  return {
    context: { propertyId: '', token: '' },
    // No property is known before init(), and a verification is only valid for
    // the address it was made on — so start unverified and let init() re-hydrate
    // each channel whose cookie matches the address actually being managed.
    email: emptyChannel(),
    sms: emptyChannel(),
    daysBefore: {},
    draft: emptyDraft(),

    init: (context) => {
      // Reset to a clean slate keyed off the current cookies so switching the
      // selected property doesn't carry over the previous one's subscriptions
      // — nor an unsaved draft made for a different address.
      set({
        context,
        email: emptyChannel(verificationIdFor('email', context.propertyId)),
        sms: emptyChannel(verificationIdFor('sms', context.propertyId)),
        daysBefore: {},
        draft: emptyDraft(),
      });
      CHANNELS.forEach((channel) => {
        const verificationId = verificationIdFor(channel, context.propertyId);
        if (verificationId) {
          seedChannel(channel, verificationId, channel === 'email');
        }
      });
    },

    isVerified: (channel) => !!get()[channel].verificationId,

    setVerified: async (channel, { verificationId, contact }) => {
      // A fresh login is a fresh baseline. The previous contact's set is
      // dropped rather than shown until the GET lands (on a shared device that
      // is one citizen's subscriptions under another's name), and so is any
      // edit made before this login: an unconfirmed change must not survive
      // into a new session (BEACON-57), least of all to be applied to somebody
      // else's subscriptions.
      set(
        (s) =>
          ({
            [channel]: { ...emptyChannel(verificationId), contact },
            draft: { ...s.draft, [channel]: null },
          }) as Partial<TStore>
      );
      // BEACON-56: this is what makes the citizen's OWN subscriptions appear on
      // every container's switches — the backend only lists them against a
      // verification_id, so logging in is the whole fix.
      await seedChannel(channel, verificationId, channel === 'email');
    },

    // BEACON-57: local only. Nothing reaches the backend until confirmPending().
    setChannel: (containerId, channel, on) => {
      set((s) => {
        const current = effectiveContainerIds(s, channel);
        const next = on
          ? Array.from(new Set([...current, containerId]))
          : current.filter((id) => id !== containerId);
        return {
          draft: {
            ...s.draft,
            // Back at the saved set ⇒ no pending change, so flipping a switch
            // on and off again leaves the Save bar closed.
            [channel]: sameSet(next, s[channel].containerIds) ? null : next,
          },
        };
      });
    },

    setDaysBefore: (containerId, days) => {
      set((s) => {
        const pendingDays = { ...s.draft.daysBefore };
        if (days === (s.daysBefore[containerId] ?? DEFAULT_DAYS_BEFORE)) {
          delete pendingDays[containerId];
        } else {
          pendingDays[containerId] = days;
        }
        return { draft: { ...s.draft, daysBefore: pendingDays } };
      });
    },

    /**
     * Send the citizen's pending changes. Only ever called from the Save
     * button — this is the confirm step BEACON-57 asks for.
     *
     * Rejects when anything could not be saved, and in that case the parts
     * that could not be saved stay pending, so Save can simply be pressed
     * again (the POST is a full replace, so a retry is idempotent).
     */
    confirmPending: async () => {
      const sent = get().draft;
      const postable = channelsToPost();
      const posted: Partial<Record<TChannel, string[]>> = {};
      postable.forEach((channel) => {
        posted[channel] = effectiveContainerIds(get(), channel);
      });

      const results = postable.length
        ? await Promise.allSettled(
            postable.map((channel) =>
              postChannel(channel, posted[channel] as string[])
            )
          )
        : [];

      const accepted = postable.filter(
        (_, i) => results[i].status === 'fulfilled'
      );
      const failed = postable.filter(
        (_, i) => results[i].status === 'rejected'
      );

      // An edit on a channel that is no longer verified — its session lapsed
      // before Save was pressed, or lapsed during it — cannot be sent at all.
      // It stays pending rather than being folded into the saved picture,
      // which would report a save that never happened. A lead-time edit counts
      // here just as a switch does: its only holder being unverified is the
      // same "nothing was sent".
      const unsendable = CHANNELS.filter(
        (channel) =>
          !accepted.includes(channel) &&
          !get()[channel].verificationId &&
          channelHasPendingEdit(get(), channel)
      );
      // A channel that is still fetching its subscriptions cannot be posted to
      // and cannot be reasoned about: the shared lead time may belong to a
      // container it carries.
      const seeding = CHANNELS.filter((channel) =>
        isChannelSeeding(get(), channel)
      );

      set((s) =>
        commitOutcome(s, {
          posted,
          sent,
          accepted,
          stillPending: [...failed, ...unsendable, ...seeding],
          baselineUnknown: seeding.length > 0,
        })
      );

      if (failed.length > 0) {
        throw (
          results.find(
            (r): r is PromiseRejectedResult => r.status === 'rejected'
          ) as PromiseRejectedResult
        ).reason;
      }
      if (unsendable.length > 0) throw new VerificationRequiredError();
      if (seeding.length > 0 && pendingChangeCount(get()) > 0) {
        throw new VerificationRequiredError();
      }
    },

    /** Throw the unsaved edits away and show the backend's picture again. */
    discardPending: () => set({ draft: emptyDraft() }),

    /**
     * Log out of ONE verified contact.
     *
     * BEACON-56 asks for a way out of a verification that does not depend on
     * the hour-long cookie expiring: a citizen on a shared device, or one who
     * typed the wrong number, needs to drop it now. The other channel's session
     * is untouched, and the pending edits made under this one go with it —
     * an unconfirmed change must not survive the session that could have saved
     * it (BEACON-57 / PRO-674).
     */
    logoutChannel: (channel) => {
      CHANNEL_COOKIES[channel].forEach(deleteCookie);
      set((s) => {
        const draft: TDraft = { ...s.draft, [channel]: null };
        const otherStillVerified = CHANNELS.some(
          (c) => c !== channel && !!s[c].verificationId
        );
        // The lead time is shared, so it only survives while some channel can
        // still save it.
        if (!otherStillVerified) draft.daysBefore = {};
        return { [channel]: emptyChannel(), draft } as Partial<TStore>;
      });
    },

    /**
     * Drop everything. Called by portal logout (src/app/User.store.tsx)
     * alongside `clearNotificationSession()`, so the next person on this
     * browser inherits neither the verified session nor its unsaved draft.
     */
    reset: () =>
      set({
        context: { propertyId: '', token: '' },
        email: emptyChannel(),
        sms: emptyChannel(),
        daysBefore: {},
        draft: emptyDraft(),
      }),
  };
});

if (process.env.NODE_ENV === 'development') {
  mountStoreDevtool('ContainerNotifications', useContainerNotificationsStore);
}
